import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useT } from '../../renderer/TextsProvider.js';
import { cssColor } from '../../sections/_shared/settingValue.js';
import { ALIGN_OPTIONS, WEIGHT_OPTIONS, marginFields, visibilityField, } from '../_shared/blockFields.js';
import { marginStyle, num, readWeight, str, visibilityCss } from '../_shared/blockRender.js';
import { useWelcomeForm } from '../_shared/welcomeForm.js';
/**
 * `welcome-submit` — LE bouton qui envoie la fenêtre de bienvenue (feature 0074, lot B).
 *
 * Distinct du bloc `button` ordinaire, qui est un LIEN : celui-ci ne va nulle part, il soumet le
 * formulaire de la carte. Les deux sont offerts dans la fenêtre, parce qu'une fenêtre peut vouloir les
 * deux — « S'inscrire » et « Voir la boutique » ne font pas la même chose.
 *
 * Il se désarme pendant l'envoi : sans cela, un double-clic sur une ligne lente inscrit deux fois et
 * compte deux visites.
 *
 * Le libellé part VIDE, pour la même raison que partout ailleurs sur cette fenêtre : vide = la
 * formulation de la boutique, dans la langue de la boutique.
 */
export const welcomeSubmitBlockSchema = {
    type: 'welcome-submit',
    name: 'Submit button',
    fields: [
        visibilityField,
        {
            id: 'label',
            type: 'text',
            label: 'Button label',
            info: 'Leave empty to keep your shop’s own wording, in your shop’s language.',
            default: '',
        },
        {
            id: 'backgroundColor',
            type: 'color',
            label: 'Background',
            group: 'Colors',
            default: { ref: 'custom', value: '#151515' },
        },
        {
            id: 'textColor',
            type: 'color',
            label: 'Text',
            group: 'Colors',
            default: { ref: 'custom', value: '#FFFFFF' },
        },
        {
            id: 'cornerRadius',
            type: 'range',
            label: 'Corner radius',
            group: 'Layout',
            min: 0,
            max: 32,
            step: 1,
            default: 10,
            unit: 'px',
        },
        {
            id: 'fontSize',
            type: 'range',
            label: 'Font size',
            group: 'Layout',
            min: 12,
            max: 22,
            step: 1,
            default: 15,
            unit: 'px',
        },
        {
            id: 'weight',
            type: 'select',
            label: 'Font weight',
            group: 'Layout',
            default: '600',
            options: WEIGHT_OPTIONS,
        },
        {
            id: 'paddingY',
            type: 'range',
            label: 'Height',
            group: 'Layout',
            info: 'The space above and below the label inside the button.',
            min: 8,
            max: 26,
            step: 1,
            default: 13,
            unit: 'px',
        },
        {
            id: 'fullWidth',
            type: 'checkbox',
            label: 'Full width',
            group: 'Layout',
            default: true,
        },
        {
            id: 'alignment',
            type: 'select',
            label: 'Alignment',
            group: 'Layout',
            default: 'center',
            options: ALIGN_OPTIONS,
            visibleWhen: { field: 'fullWidth', equals: false },
        },
        ...marginFields(3, 9),
    ],
};
/** left / center / right → la `justify-content` de la ligne qui porte le bouton. */
function justify(value) {
    if (value === 'left')
        return 'flex-start';
    if (value === 'right')
        return 'flex-end';
    return 'center';
}
const WelcomeSubmitBlock = ({ block }) => {
    const bs = block.settings;
    const cls = `aqx-wps-${block.id}`;
    const t = useT();
    const { busy } = useWelcomeForm();
    const full = bs.fullWidth !== false;
    const label = str(bs.label).trim() || t('footer.subscribe');
    const weight = readWeight(bs);
    const rowStyle = {
        display: 'flex',
        justifyContent: full ? 'stretch' : justify(bs.alignment),
        ...marginStyle(bs, 3, 9),
    };
    const buttonStyle = {
        inlineSize: full ? '100%' : 'auto',
        boxSizing: 'border-box',
        cursor: busy ? 'progress' : 'pointer',
        border: 'none',
        background: cssColor(bs.backgroundColor, '#151515'),
        color: cssColor(bs.textColor, '#FFFFFF'),
        borderRadius: `${num(bs.cornerRadius, 10)}px`,
        fontSize: `${num(bs.fontSize, 15)}px`,
        fontWeight: weight === '' ? 600 : Number(weight),
        fontFamily: 'inherit',
        padding: `${num(bs.paddingY, 13)}px 18px`,
        opacity: busy ? 0.65 : 1,
    };
    const css = visibilityCss(cls, bs.visibility);
    return (_jsxs("div", { className: cls, style: rowStyle, children: [css ? _jsx("style", { dangerouslySetInnerHTML: { __html: css } }) : null, _jsx("button", { type: "submit", style: buttonStyle, disabled: busy, children: label })] }));
};
export const welcomeSubmitBlock = {
    type: 'welcome-submit',
    schema: welcomeSubmitBlockSchema,
    Component: WelcomeSubmitBlock,
    meta: { label: 'Submit button', icon: 'send' },
};
//# sourceMappingURL=index.js.map